import type { Socket } from 'socket.io-client';
import { io } from 'socket.io-client';
import type { ArafClientToServerEvents, ArafServerToClientEvents } from './types';

export type GatewaySocket = Socket<ArafServerToClientEvents, ArafClientToServerEvents>;

const DEFAULT_GATEWAY_URL = (): string => {
  const explicit = import.meta.env.VITE_GATEWAY_URL as string | undefined;
  if (explicit) return `${explicit}/gateway`;

  const base = import.meta.env.VITE_ARAF_FACTORY_URL as string | undefined;
  return `${base ?? 'http://localhost:3550'}/gateway`;
};

let _socket: GatewaySocket | null = null;

function attachDevLogger(sock: GatewaySocket): void {
  sock.onAny((eventName: string, ...args: unknown[]) => {
    const payload = args[0] ?? {};
    const traceId =
      payload !== null && typeof payload === 'object' && 'traceId' in payload
        ? (payload as Record<string, unknown>).traceId
        : undefined;

    const isError =
      eventName.toLowerCase().includes('error') ||
      (payload !== null && typeof payload === 'object' && 'error' in payload);

    const color = isError ? '#ef9a9a' : '#81c784';
    const label = isError ? `[WS ↓ ERR] ${eventName}` : `[WS ↓] ${eventName}`;

    console.groupCollapsed(`%c${label}`, `color: ${color}; font-weight: bold`);
    console.log('payload:', payload);
    if (traceId !== undefined) console.log('traceId:', traceId);
    console.groupEnd();
  });

  const originalEmit = sock.emit.bind(sock);
  // biome-ignore lint/suspicious/noExplicitAny: overriding variadic emit
  (sock as unknown as Record<string, any>).emit = function devEmit(
    eventName: string,
    ...args: unknown[]
  ): boolean {
    const payload = args[0] ?? {};
    const traceId =
      payload !== null && typeof payload === 'object' && 'traceId' in payload
        ? (payload as Record<string, unknown>).traceId
        : undefined;

    const isError =
      eventName.toLowerCase().includes('error') ||
      (payload !== null && typeof payload === 'object' && 'error' in payload);

    const color = isError ? '#ef9a9a' : '#4fc3f7';
    const label = isError ? `[WS ↑ ERR] ${eventName}` : `[WS ↑] ${eventName}`;

    console.groupCollapsed(`%c${label}`, `color: ${color}; font-weight: bold`);
    console.log('payload:', payload);
    if (traceId !== undefined) console.log('traceId:', traceId);
    console.groupEnd();

    // biome-ignore lint/suspicious/noExplicitAny: variadic emit args bypass typed overloads intentionally
    (originalEmit as (ev: string, ...a: any[]) => void)(eventName, ...args);
    return true;
  };
}

export function getGatewaySocket(
  token: string,
  sessionId?: string | null,
  gatewayUrl?: string,
): GatewaySocket {
  if (_socket) return _socket;

  const url = gatewayUrl ?? DEFAULT_GATEWAY_URL();

  _socket = io(url, {
    auth: {
      token: `Bearer ${token}`,
      ...(sessionId ? { sessionId } : {}),
    },
    reconnection: true,
    reconnectionDelay: 1000,
    reconnectionDelayMax: 10_000,
    reconnectionAttempts: Infinity,
    transports: ['websocket'],
  }) as GatewaySocket;

  if (import.meta.env.DEV) {
    attachDevLogger(_socket);
  }

  return _socket;
}

export function reconnectGatewaySocket(freshToken: string, sessionId: string): GatewaySocket {
  if (_socket) {
    _socket.disconnect();
    _socket = null;
  }
  return getGatewaySocket(freshToken, sessionId);
}

export function destroyGatewaySocket(): void {
  if (_socket) {
    _socket.disconnect();
    _socket = null;
  }
}

export function peekGatewaySocket(): GatewaySocket | null {
  return _socket;
}
